/**
 * @kehto/acl — Pure check function.
 *
 * Determines whether an identity has a specific capability.
 * No side effects, no I/O, no mutations.
 */

import type { AclState, Identity } from './types.js';

/**
 * Compute composite key from identity fields.
 *
 * Under NIP-5D v0.1.0, the key is 'dTag:hash' (pubkey is ignored).
 *
 * @param identity - Napplet identity
 * @returns Composite key string 'dTag:hash'
 *
 * @example
 * ```ts
 * toKey({ dTag: 'chat', hash: 'ff00' })
 * // => 'chat:ff00'
 * ```
 */
export function toKey(identity: Identity): string {
  return `${identity.dTag}:${identity.hash}`;
}

/**
 * Check whether an identity has a specific capability.
 *
 * Decision logic:
 * 1. If identity has no entry: return based on defaultPolicy
 *    - 'permissive' → true (all caps granted to unknown identities)
 *    - 'restrictive' → false (all caps denied to unknown identities)
 * 2. If identity is blocked: return false (blocked overrides all caps)
 * 3. Otherwise: return (entry.caps & cap) !== 0
 *
 * @param state - Current ACL state (immutable)
 * @param identity - Napplet identity to check
 * @param cap - Capability bit constant (e.g., CAP_RELAY_READ)
 * @returns true if the identity has the capability, false otherwise
 *
 * @example
 * ```ts
 * import { check, createState, grant } from '@kehto/acl';
 * import { CAP_RELAY_READ } from '@kehto/acl';
 *
 * const state = createState('restrictive');
 * const id = { dTag: 'chat', hash: 'ff00' };
 *
 * check(state, id, CAP_RELAY_READ); // false (restrictive, no entry)
 *
 * const state2 = grant(state, id, CAP_RELAY_READ);
 * check(state2, id, CAP_RELAY_READ); // true
 * ```
 */
export function check(state: AclState, identity: Identity, cap: number): boolean {
  const key = toKey(identity);
  const entry = state.entries[key];
  if (!entry) {
    return state.defaultPolicy === 'permissive';
  }
  if (entry.blocked) {
    return false;
  }
  return (entry.caps & cap) !== 0;
}
